"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Les "corrections" ratées de l'IA : aucune ne centre le bouton.
const VIBE_STATES = [
    { just: "justify-start", full: false, msgKey: "Html_loop_ai_msg_1" },
    { just: "justify-start", full: true, msgKey: "Html_loop_ai_msg_2" },
    { just: "justify-end", full: false, msgKey: "Html_loop_ai_msg_3" },
];

export default function HtmlCssAiLoop() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [tab, setTab] = useState("vibe"); // vibe | vous
    const [attempts, setAttempts] = useState(1);
    const [fixed, setFixed] = useState(false);

    const inVibe = tab === "vibe";
    const idx = (attempts - 1) % VIBE_STATES.length;
    const vibe = VIBE_STATES[idx];

    const containerJustify = inVibe
        ? vibe.just
        : fixed
          ? "justify-center"
          : "justify-start";
    const btnFull = inVibe ? vibe.full : false;
    const centered = !inVibe && fixed;

    return (
        <section id="htmlcss-ai-loop" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Html_loop_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Html_loop_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (HTML orange + CSS bleu) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-orange-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <SolarIcon
                            name="code-square-bold"
                            className="w-3.5 h-3.5"
                        />
                        bouton.html
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-orange-500/15 ring-1 ring-orange-400/30 px-2.5 py-0.5">
                        <SolarIcon
                            name="refresh-bold"
                            className="w-3 h-3 text-orange-300"
                        />
                        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-orange-300">
                            {t("Html_loop_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7">
                    {/* Onglets */}
                    <div className="flex justify-center mb-7">
                        <div className="inline-flex items-center gap-1 rounded-full bg-black/30 ring-1 ring-white/10 p-1">
                            <button
                                type="button"
                                onClick={() => setTab("vibe")}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    inVibe
                                        ? "bg-rose-500/90 text-white"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Html_loop_tab_vibe")}
                            </button>
                            <button
                                type="button"
                                onClick={() => setTab("vous")}
                                className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                                    !inVibe
                                        ? "bg-emerald-500 text-white"
                                        : "text-gray-400 hover:text-gray-200"
                                }`}
                            >
                                {t("Html_loop_tab_you")}
                            </button>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6 items-start">
                        {/* APERÇU partagé */}
                        <div className="min-w-0">
                            <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
                                <div className="mb-4">
                                    <div className="text-slate-900 font-bold text-sm">
                                        {t("Html_loop_card_title")}
                                    </div>
                                    <div className="text-slate-500 text-xs">
                                        {t("Html_loop_card_subtitle")}
                                    </div>
                                </div>
                                <div
                                    className={`flex ${containerJustify} rounded-lg border border-dashed border-slate-300 p-3 transition-all duration-300`}
                                >
                                    <button
                                        type="button"
                                        className={`${btnFull ? "w-full" : ""} inline-flex items-center justify-center rounded-lg bg-[#2563eb] text-white px-5 py-2 text-sm font-medium transition-all duration-300`}
                                    >
                                        {t("Html_loop_card_button")}
                                    </button>
                                </div>
                                <div className="mt-3 flex items-center gap-1.5 text-xs font-medium">
                                    {centered ? (
                                        <span className="inline-flex items-center gap-1.5 text-emerald-600">
                                            <SolarIcon
                                                name="check-circle-bold"
                                                className="w-4 h-4"
                                            />
                                            {t("Html_loop_verdict_ok")}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 text-rose-500">
                                            <SolarIcon
                                                name="close-circle-bold"
                                                className="w-4 h-4"
                                            />
                                            {t("Html_loop_verdict_ko")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* INTERACTION selon l'onglet */}
                        <div className="min-w-0">
                            {inVibe ? (
                                <div className="flex flex-col">
                                    {/* Message utilisateur */}
                                    <div className="flex justify-end mb-2.5">
                                        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-sky-500/15 ring-1 ring-sky-400/20 px-3.5 py-2 text-sm text-sky-100">
                                            {t("Html_loop_user_msg")}
                                        </div>
                                    </div>
                                    {/* Réponse IA (change à chaque tentative) */}
                                    <div className="flex items-start gap-2.5">
                                        <Image
                                            src="/images/bebot/bebot-profile.jpg"
                                            width={32}
                                            height={32}
                                            alt="BeBot"
                                            className="w-8 h-8 shrink-0 rounded-full object-cover"
                                        />
                                        <motion.div
                                            key={attempts}
                                            initial={
                                                reduce
                                                    ? false
                                                    : { opacity: 0, y: 6 }
                                            }
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.25 }}
                                            className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white/[0.06] ring-1 ring-white/10 px-3.5 py-2 text-sm text-gray-200"
                                        >
                                            {t(vibe.msgKey)}
                                        </motion.div>
                                    </div>

                                    {/* Compteur */}
                                    <div className="mt-4 flex items-center gap-2 font-mono text-xs text-gray-400">
                                        <span>
                                            {t("Html_loop_attempts_label")} :{" "}
                                            <b className="text-rose-300 tabular-nums">
                                                {attempts}
                                            </b>
                                        </span>
                                        <span className="text-gray-600">·</span>
                                        <span className="text-rose-400">
                                            {t("Html_loop_still_broken")}
                                        </span>
                                    </div>

                                    {attempts >= 3 && (
                                        <motion.div
                                            initial={
                                                reduce
                                                    ? false
                                                    : { opacity: 0 }
                                            }
                                            animate={{ opacity: 1 }}
                                            className="mt-2 text-xs text-amber-300/90"
                                        >
                                            {t("Html_loop_loop_note")}
                                        </motion.div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() =>
                                            setAttempts((a) => a + 1)
                                        }
                                        className="mt-4 self-start inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-4 py-2 text-sm font-semibold text-gray-200 transition active:scale-95"
                                    >
                                        <SolarIcon
                                            name="refresh-bold"
                                            className="w-4 h-4"
                                        />
                                        {t("Html_loop_retry")}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col">
                                    <p className="text-sm text-gray-300 leading-relaxed mb-3">
                                        {t("Html_loop_you_lead")}
                                    </p>
                                    <div className="rounded-xl bg-black/30 ring-1 ring-white/10 p-4 font-mono text-[12.5px] sm:text-[13px] leading-relaxed">
                                        <div>
                                            <span className="text-amber-200">
                                                .carte
                                            </span>
                                            <span className="text-gray-400">
                                                {" {"}
                                            </span>
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-sky-300">
                                                display
                                            </span>
                                            <span className="text-gray-400">
                                                :
                                            </span>
                                            <span className="text-amber-300">
                                                flex
                                            </span>
                                            <span className="text-gray-400">
                                                ;
                                            </span>
                                        </div>
                                        <div className="pl-4">
                                            <span className="text-sky-300">
                                                justify-content
                                            </span>
                                            <span className="text-gray-400">
                                                :
                                            </span>
                                            {fixed ? (
                                                <span className="rounded bg-emerald-400/15 ring-1 ring-emerald-400/40 px-1 text-emerald-300">
                                                    center
                                                </span>
                                            ) : (
                                                <span className="rounded bg-rose-400/15 ring-1 ring-rose-400/40 px-1 text-rose-300">
                                                    flex-start
                                                </span>
                                            )}
                                            <span className="text-gray-400">
                                                ;
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">
                                                {"}"}
                                            </span>
                                        </div>
                                    </div>

                                    {fixed ? (
                                        <div className="mt-4 flex items-center justify-between gap-3">
                                            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300">
                                                <SolarIcon
                                                    name="check-circle-bold"
                                                    className="w-4 h-4"
                                                />
                                                {t("Html_loop_fixed_msg")}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setFixed(false)}
                                                className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3 py-1.5 text-xs text-gray-300 transition active:scale-95"
                                            >
                                                <SolarIcon
                                                    name="restart-bold-duotone"
                                                    className="w-3.5 h-3.5"
                                                />
                                                {t("Html_loop_reset")}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setFixed(true)}
                                            className="mt-4 self-start inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 px-4 py-2 text-sm font-semibold text-white transition active:scale-95"
                                        >
                                            <SolarIcon
                                                name="pen-new-square-bold"
                                                className="w-4 h-4"
                                            />
                                            {t("Html_loop_fix_button")}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Conclusion */}
                    <div className="mt-7 flex items-start gap-3 rounded-2xl bg-orange-500/10 ring-1 ring-orange-400/25 px-5 py-4">
                        <SolarIcon
                            name="bolt-bold-duotone"
                            className="w-5 h-5 text-orange-300 shrink-0 mt-0.5"
                        />
                        <p className="text-[15px] font-medium text-white leading-snug mb-0">
                            {t("Html_loop_conclusion")}
                        </p>
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Html_loop_caption")}
            </p>
        </section>
    );
}
